import { groupBy } from 'lodash';
import { PaginatedMonthContributions } from '../../api/calculators/getCalculatorsUserAportations';
import {
	ContributionFromEnum,
	ContributionStatusEnum,
	SingleUserContributionData
} from '../../api/users/interfaces/ContributionData';
import { ContributionCategoryEnum, classifyContribution } from '../contributionClassification';
import { anioDeMadrid } from './formato';
import { TipoPlanResumen } from './planes';

/**
 * Los movimientos de UN plan, pedidos con su `userEppConfigId` (chirigota#585, Fase A2).
 *
 * 🔴 Por qué uno a uno y no una llamada con todos los `productCodes` (lo que decía el análisis,
 * §5b.2): sin `userEppConfigId`, comparsa filtra por la empresa del token y cuela las filas sin
 * `productCode`. A quien tiene empleo y plan personal se le perdían movimientos del personal, y
 * las filas sin plan no se pueden asignar a ninguno. Con `userEppConfigId` el filtro es estricto
 * y por plan. Las filas sin `productCode` quedan fuera (decidido el 19/10): nada se reparte ni
 * se duplica.
 *
 * Reglas de tipos: comparsa `docs/CONTRIBUCIONES_Y_RESCATES.md`. Solo
 * `CANCELLMENT_PARTICIPANT_SAVINGS` es rescate; `_EXTRAORDINARY` y `_PROMOTER_SAVINGS` son
 * correcciones que netean.
 */

/** La respuesta de `user-aportations-by-type` viene por meses; los de bonus ya están dentro. */
export const aplanarMovimientos = (
	meses: PaginatedMonthContributions[] | undefined
): SingleUserContributionData[] => (meses ?? []).flatMap((mes) => mes.contributions);

export interface ResumenDeMovimientos {
	/** € aportados este año que cuentan para el límite legal. */
	aportadoAnio: number;
	rescatadoAnio: number;
	rescatadoTotal: number;
	/**
	 * Base de coste de los traspasos externos de ESTE plan (entradas − salidas, nunca negativa).
	 * Es lo que `user-cost-basis` calcula, pero sin su filtro por empresa ni las filas sin plan.
	 */
	costeTraspasos: number;
	/**
	 * Lo que entró en el plan este año (desde la foto de partida, con `flujoDesde`) menos lo que
	 * salió, para la rentabilidad del año: lo que entra no es ganancia y lo que sale no es pérdida.
	 */
	flujoNetoAnio: number;
	/**
	 * Cuántos movimientos confirmados tiene el plan, de toda su vida (sin mirar el año). Con 0 y
	 * saldo 0 el plan está VACÍO: aún no tiene nada, y eso no es «sin dato» (product#58).
	 */
	confirmados: number;
}

/**
 * Cómo cuenta cada tipo en el dinero que entra (+1) o sale (−1) del plan. Con el importe tal cual
 * llega: Caser manda las salidas en positivo, y las correcciones y los reversos internos ya
 * vienen en negativo. Los internos van en pareja (rebalanceo del ciclo de vida dentro del plan;
 * medido en PROD el 19/10/2026: 1.513 entradas y 1.513 salidas, y 5 + 5 reversos), y se anulan.
 *
 * Un `Record` completo a propósito: un tipo nuevo no compila hasta decidir cómo cuenta.
 */
export const SENTIDO_DEL_FLUJO: Record<ContributionFromEnum, 1 | -1> = {
	[ContributionFromEnum.PARTICIPANT]: 1,
	[ContributionFromEnum.COMPANY]: 1,
	[ContributionFromEnum.CANCELLMENT_PARTICIPANT_SAVINGS_EXTRAORDINARY]: 1,
	[ContributionFromEnum.CANCELLMENT_PROMOTER_SAVINGS]: 1,
	[ContributionFromEnum.TRANSFER_EXTERNAL_INPUT]: 1,
	[ContributionFromEnum.TRANSFER_INTERNAL_INPUT]: 1,
	[ContributionFromEnum.CANCELLMENT_INTERNAL_INPUT]: 1,
	[ContributionFromEnum.CANCELLMENT_PARTICIPANT_SAVINGS]: -1,
	[ContributionFromEnum.TRANSFER_EXTERNAL_OUTPUT]: -1,
	[ContributionFromEnum.TRANSFER_INTERNAL_OUTPUT]: -1,
	[ContributionFromEnum.CANCELLMENT_INTERNAL_OUTPUT]: -1
};

const suma = (movimientos: SingleUserContributionData[]) =>
	movimientos.reduce((total, m) => total + m.contributionAmount, 0);

export function resumenDeMovimientos(
	movimientos: SingleUserContributionData[],
	{
		tipo,
		hoy,
		flujoDesde = null,
		anio: anioPedido
	}: {
		tipo: TipoPlanResumen;
		hoy: Date;
		/**
		 * `AAAA` del que se quieren lo aportado y lo rescatado (el selector de año del Resumen). Sin él,
		 * el año en curso de Madrid.
		 */
		anio?: string;
		/**
		 * El día (`AAAA-MM-DD`) de la foto del saldo a 1 de enero (`vestedRightsAtYearStartDate`):
		 * lo que entra y sale se cuenta desde el día siguiente. Si la sync de fin de año falló, la
		 * foto es anterior al 1 de enero y lo aportado esos días no está en ella. Sin él, el año natural.
		 */
		flujoDesde?: string | null;
	}
): ResumenDeMovimientos {
	const confirmados = movimientos.filter(
		(m) => m.statusContribution === ContributionStatusEnum.CONFIRMED
	);
	const anio = anioPedido ?? String(anioDeMadrid(hoy));
	const delAnio = confirmados.filter((m) => m.dateContribution.slice(0, 4) === anio);
	const deTipo = (lista: SingleUserContributionData[], tipos: ContributionFromEnum[]) =>
		lista.filter((m) => tipos.includes(m.typeContribution));
	const deCategoria = (
		lista: SingleUserContributionData[],
		categorias: ContributionCategoryEnum[]
	) =>
		lista.filter((m) => {
			const categoria = classifyContribution(m.typeContribution);
			return categoria !== null && categorias.includes(categoria);
		});

	// Para el límite cuenta lo propio y, en un plan de EMPLEO, también lo de la empresa: el tope
	// de 8.500 € del art. 5.3 RDL 1/2020 incluye las contribuciones empresariales.
	const cuentanParaLimite =
		tipo === 'EMPLEO'
			? [ContributionCategoryEnum.PARTICIPANT, ContributionCategoryEnum.COMPANY]
			: [ContributionCategoryEnum.PARTICIPANT];
	const rescate = [ContributionCategoryEnum.RESCATE];

	return {
		aportadoAnio: suma(deCategoria(delAnio, cuentanParaLimite)),
		rescatadoAnio: suma(deCategoria(delAnio, rescate)),
		rescatadoTotal: suma(deCategoria(confirmados, rescate)),
		flujoNetoAnio: (flujoDesde
			? confirmados.filter((m) => m.dateContribution.slice(0, 10) > flujoDesde)
			: delAnio
		).reduce(
			(total, m) => total + SENTIDO_DEL_FLUJO[m.typeContribution] * m.contributionAmount,
			0
		),
		costeTraspasos: Math.max(
			suma(deTipo(confirmados, [ContributionFromEnum.TRANSFER_EXTERNAL_INPUT])) -
				suma(deTipo(confirmados, [ContributionFromEnum.TRANSFER_EXTERNAL_OUTPUT])),
			0
		),
		confirmados: confirmados.length
	};
}

/** `tuEmpresa`: lo tuyo y lo de la empresa del mismo plan y el mismo día, en una sola fila (Proa). */
export type OrigenMovimiento = 'tu' | 'empresa' | 'tuEmpresa' | 'traspaso' | 'rescate';

export interface MovimientoResumen {
	id: string;
	/** `YYYY-MM-DD`. */
	fecha: string;
	planId: string;
	planNombre: string;
	/** Con signo: lo que sale del plan (rescate, traspaso de salida) va en negativo. */
	importe: number;
	origen: OrigenMovimiento;
}

/**
 * Lo que se lista y cómo. Las correcciones netean y los movimientos internos de Caser son
 * reversos: no son operaciones de la persona, así que no se listan (sí cuentan en las sumas).
 * Caser manda los rescates y las salidas en positivo: el signo se pone aquí.
 */
const COMO_SE_LISTA: Partial<
	Record<ContributionFromEnum, { origen: OrigenMovimiento; signo: 1 | -1 }>
> = {
	[ContributionFromEnum.PARTICIPANT]: { origen: 'tu', signo: 1 },
	[ContributionFromEnum.COMPANY]: { origen: 'empresa', signo: 1 },
	[ContributionFromEnum.TRANSFER_EXTERNAL_INPUT]: { origen: 'traspaso', signo: 1 },
	[ContributionFromEnum.TRANSFER_EXTERNAL_OUTPUT]: { origen: 'traspaso', signo: -1 },
	[ContributionFromEnum.CANCELLMENT_PARTICIPANT_SAVINGS]: { origen: 'rescate', signo: -1 }
};

const APORTACION: OrigenMovimiento[] = ['tu', 'empresa'];

/**
 * La aportación de un mes en empleo llega como dos movimientos (lo tuyo y lo de la empresa) y se
 * lista como UNA, «Tú + empresa», como en Proa. Solo se junta lo del mismo plan y el mismo día, y
 * solo si están las dos partes; la fila juntada ocupa el sitio de la primera.
 */
function juntarAportaciones(lista: MovimientoResumen[]): MovimientoResumen[] {
	const clave = (m: MovimientoResumen) => `${m.planId}:${m.fecha}`;
	const grupos = groupBy(
		lista.filter((m) => APORTACION.includes(m.origen)),
		clave
	);
	return lista.flatMap((m) => {
		const grupo = APORTACION.includes(m.origen) ? grupos[clave(m)] : undefined;
		if (!grupo || !APORTACION.every((origen) => grupo.some((g) => g.origen === origen))) {
			return [m];
		}
		if (grupo[0] !== m) return [];
		return [
			{
				...m,
				id: `${clave(m)}:tuEmpresa`,
				importe: grupo.reduce((total, g) => total + g.importe, 0),
				origen: 'tuEmpresa' as const
			}
		];
	});
}

/** Los N movimientos más recientes de todos los planes, cada uno con el nombre de su plan. */
export function ultimosMovimientos(
	porPlan: { plan: { id: string; nombre: string }; movimientos: SingleUserContributionData[] }[],
	cuantos: number,
	/** `AAAA`: solo los de ese año (el selector de año del Resumen). Sin él, de siempre. */
	anio?: string
): MovimientoResumen[] {
	const lista = porPlan
		.flatMap(({ plan, movimientos }) =>
			movimientos.flatMap((m) => {
				const como = COMO_SE_LISTA[m.typeContribution];
				if (!como || m.statusContribution !== ContributionStatusEnum.CONFIRMED) return [];
				if (anio && !m.dateContribution.startsWith(`${anio}-`)) return [];
				return [
					{
						// El id de Caser no es único entre planes: va con el del plan.
						id: `${plan.id}:${m.id}`,
						fecha: m.dateContribution.slice(0, 10),
						planId: plan.id,
						planNombre: plan.nombre,
						importe: como.signo * m.contributionAmount,
						origen: como.origen
					}
				];
			})
		)
		.sort((a, b) => b.fecha.localeCompare(a.fecha));
	return juntarAportaciones(lista).slice(0, cuantos);
}
